// src/plan/Plan.jsx
import { useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import AppModal from "../components/AppModal";
import CustomDataTable from "../components/DataTable";
import { FiEdit, FiTrash2, FiPlus, FiCheckCircle, FiXCircle, FiHome } from "react-icons/fi";
import "./HomeTabs.css";

export default function HomeTabs({ darkMode }) {
    const [show, setShow] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedTab, setSelectedTab] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        tab_name: '',
        status: 'inactive' // Default to unpublished
    });

    // Fetch tabs from API
    useEffect(() => {
        fetchTabs();
    }, []);

    const fetchTabs = async () => {
        setLoading(true);
        try {
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                : process.env.REACT_APP_BACKEND_USER_API;
            const response = await fetch(`${base_url}/api/hometab/home-tabs`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            const data = await response.json();
            setTabs(data.data || data || []);
        } catch (error) {
            console.error('Error fetching tabs:', error);
            setTabs([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle status change
    const handleStatusChange = async (tab, newStatus) => {
        const previousStatus = tab.status;
        setUpdatingStatusId(tab.id || tab._id);
        
        // Optimistic update
        setTabs(prev =>
            prev.map(item =>
                (item.id === tab.id || item._id === tab._id) ? { ...item, status: newStatus } : item
            )
        );

        try {
            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                : process.env.REACT_APP_BACKEND_USER_API;
            
            const tabId = tab.id || tab._id;
            
            const response = await fetch(`${base_url}/api/hometab/update-tab-status`, {
                method: 'PUT',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify({ 
                    id: tabId,
                    status: newStatus 
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Update failed ${response.status}`);
            }
            
            // Refresh to get latest data
            await fetchTabs();
        } catch (error) {
            console.error('Error updating tab status:', error);
            // Revert on error
            setTabs(prev =>
                prev.map(item =>
                    (item.id === tab.id || item._id === tab._id) ? { ...item, status: previousStatus } : item
                )
            );
        } finally {
            setUpdatingStatusId(null);
        }
    };

    // Handle add/edit tab submit
    const handleSubmitTab = async () => {
        // Validate tab_name
        if (!formData.tab_name.trim()) {
            alert('Please enter a tab name');
            return;
        }

        setSubmitting(true);
        
        try {
            const payload = {
                tab_name: formData.tab_name.trim(),
                status: formData.status
            };

            const base_url = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                : process.env.REACT_APP_BACKEND_USER_API;

            // If editing, include the ID
            if (selectedTab) {
                payload.id = selectedTab.id || selectedTab._id;
            }

            const url = selectedTab 
                ? `${base_url}/api/hometab/update-home-tab` 
                : `${base_url}/api/hometab/submit-home-tab`;
            
            const method = selectedTab ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify(payload)
            });

            const responseData = await response.json();

            if (response.ok) {
                await fetchTabs();
                setShow(false);
                setSelectedTab(null);
                setFormData({ tab_name: '', status: 'inactive' });
                alert(selectedTab ? 'Tab updated successfully!' : 'Tab added successfully!');
            } else {
                console.error('Error saving tab:', responseData);
                alert(responseData.message || 'Failed to save tab. Please try again.');
            }
        } catch (error) {
            console.error('Error saving tab:', error);
            alert('An error occurred while saving the tab. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete tab
    const handleDelete = async () => {
        if (selectedTab) {
            setSubmitting(true);
            try {
                const base_url = process.env.REACT_APP_ENV === 'local' 
                    ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                    : process.env.REACT_APP_BACKEND_USER_API;
                
                const tabId = selectedTab.id || selectedTab._id;
                    
                const response = await fetch(`${base_url}/api/hometab/delete-home-tab`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    },
                    body: JSON.stringify({ id: tabId })
                });
                
                if (response.ok) {
                    await fetchTabs();
                    setShowDeleteModal(false);
                    setSelectedTab(null);
                    alert('Tab deleted successfully!');
                } else {
                    const errorData = await response.json();
                    console.error('Error deleting tab:', errorData);
                    alert(errorData.message || 'Failed to delete tab. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting tab:', error);
                alert('An error occurred while deleting the tab. Please try again.');
            } finally {
                setSubmitting(false);
            }
        }
    };

    const columns = [
        {
            name: "Tab Name",
            selector: row => row.tab_name || row.name,
            sortable: true,
            cell: row => <span className="tab-name-cell">{row.tab_name || row.name}</span>,
        },
        {
            name: "Status",
            selector: row => row.status,
            sortable: true,
            cell: row => {
                const isUpdating = updatingStatusId === (row.id || row._id);
                const status = row.status || 'inactive';
                
                return (
                    <div className="status-select-wrapper">
                        <select
                            className={`status-select ${status}`}
                            value={status}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            disabled={isUpdating}
                        >
                            <option value="active">Published</option>
                            <option value="inactive">Unpublished</option>
                        </select>
                        {isUpdating && (
                            <span className="status-updating">Updating...</span>
                        )}
                    </div>
                );
            },
        },
        {
            name: "Created At",
            selector: row => row.created_at || row.createdAt || '-',
            sortable: true,
            cell: row => <span>{row.created_at || row.createdAt || '-'}</span>,
        },
        {
            name: "Actions",
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="action-btn edit" 
                        onClick={() => handleEdit(row)}
                        title="Edit Tab"
                    >
                        <FiEdit size={14} />
                    </button>
                    <button 
                        className="action-btn delete" 
                        onClick={() => handleDeleteClick(row)}
                        title="Delete Tab"
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    // Handle edit tab
    const handleEdit = (tab) => {
        setSelectedTab(tab);
        setFormData({
            tab_name: tab.tab_name || tab.name || '',
            status: tab.status || 'inactive'
        });
        setShow(true);
    };

    // Handle delete click
    const handleDeleteClick = (tab) => {
        setSelectedTab(tab);
        setShowDeleteModal(true);
    };

    // Handle add tab
    const handleAddTab = () => {
        setSelectedTab(null);
        setFormData({ tab_name: '', status: 'inactive' });
        setShow(true);
    };

    // Filter tabs based on search
    const filteredData = tabs.filter(item =>
        (item.tab_name || item.name || '').toLowerCase().includes(search.toLowerCase())
    );

    // Get counts for stats
    const totalTabs = tabs.length;
    const activeTabs = tabs.filter(t => t.status === "active").length;
    const inactiveTabs = tabs.filter(t => t.status !== "active" && t.status !== undefined).length;

    return (
        <div className={`plans-container ${darkMode ? 'dark' : ''}`}>
            <div className="plans-content">
                {/* Page Header */}
                <PageHeader
                    title="Home Tabs"
                    buttonText="Add Tab"
                    onAdd={handleAddTab}
                />

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <FiHome size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Tabs</span>
                            <span className="stat-value">{totalTabs}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon active">
                            <FiCheckCircle size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Published</span>
                            <span className="stat-value">{activeTabs}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon inactive">
                            <FiXCircle size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Unpublished</span>
                            <span className="stat-value">{inactiveTabs}</span>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="table-card-modern">
                    {/* Search */}
                    <div className="table-toolbar">
                        <div className="search-wrapper">
                            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input
                                className="search-input-modern"
                                placeholder="Search tabs by name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="table-info">
                            Showing {filteredData.length} of {tabs.length} tabs
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-wrapper-modern">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading tabs...</p>
                            </div>
                        ) : (
                            <CustomDataTable
                                columns={columns}
                                data={filteredData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            <AppModal
                show={show}
                title={selectedTab ? "Edit Tab" : "Add Tab"}
                onClose={() => {
                    setShow(false);
                    setSelectedTab(null);
                    setFormData({ tab_name: '', status: 'inactive' });
                }}
            >
                <div className="modal-form">
                    <div className="form-group">
                        <label className="form-label">Tab Name</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Enter tab name"
                            value={formData.tab_name}
                            onChange={(e) => setFormData({ ...formData, tab_name: e.target.value })}
                            disabled={submitting}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && formData.tab_name.trim()) {
                                    handleSubmitTab();
                                }
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status</label>
                        <div className="status-toggle-group">
                            <button
                                type="button"
                                className={`status-option ${formData.status === 'active' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, status: 'active' })}
                                disabled={submitting}
                            >
                                <FiCheckCircle size={16} />
                                Published
                            </button>
                            <button
                                type="button"
                                className={`status-option ${formData.status === 'inactive' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, status: 'inactive' })}
                                disabled={submitting}
                            >
                                <FiXCircle size={16} />
                                Unpublished
                            </button>
                        </div>
                    </div>
                    
                    {/* Footer with submit button */}
                    <div className="modal-footer" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                        <button 
                            className="modal-cancel-btn" 
                            onClick={() => {
                                setShow(false);
                                setSelectedTab(null);
                                setFormData({ tab_name: '', status: 'inactive' });
                            }}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button 
                            className="modal-submit-btn" 
                            onClick={handleSubmitTab}
                            disabled={!formData.tab_name.trim() || submitting}
                        >
                            {submitting ? 'Saving...' : (selectedTab ? 'Update Tab' : 'Add Tab')}
                        </button>
                    </div>
                </div>
            </AppModal>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedTab && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Delete Tab</h3>
                            <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirm-text">
                                Are you sure you want to delete the tab <strong>"{selectedTab.tab_name || selectedTab.name}"</strong>?
                            </p>
                            <p className="delete-warning-text">
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-cancel-btn" 
                                onClick={() => setShowDeleteModal(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button 
                                className="modal-delete-btn" 
                                onClick={handleDelete}
                                disabled={submitting}
                            >
                                {submitting ? 'Deleting...' : 'Delete Tab'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}